/**
 * ThemeFontSelect - Font selector with preview
 * Adapted from shadcn-studio
 *
 * Uses centralized font config from @optidev/shared-types
 */

import { useMemo, useState, useEffect } from "react";

import {
  CSS_FONTS,
  SANS_SERIF_FONTS,
  SERIF_FONTS,
  MONOSPACE_FONTS,
  DEFAULT_FONT_SANS,
  DEFAULT_FONT_SERIF,
  DEFAULT_FONT_MONO,
} from "@optidev/shared-types";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Re-export from shared-types for backward compatibility
export const fonts = CSS_FONTS;
export const sansSerifFonts = SANS_SERIF_FONTS;
export const serifFonts = SERIF_FONTS;
export const monoFonts = MONOSPACE_FONTS;
export { DEFAULT_FONT_SANS, DEFAULT_FONT_SERIF, DEFAULT_FONT_MONO };

export function getAppliedThemeFont(
  fontValue: string | undefined,
  _fontKey: string
): string | null {
  if (!fontValue) return null;

  // Find key of font in fonts object based on value
  const key = Object.keys(fonts).find((fontName) =>
    fonts[fontName].includes(fontValue.split(",")[0].replace(/['"]/g, "").trim())
  );

  return key || null;
}

interface ThemeFontSelectProps {
  fonts: Record<string, string>;
  defaultValue: string;
  currentFont: string | null;
  onFontChange: (font: string) => void;
}

export function ThemeFontSelect({
  fonts: fontOptions,
  defaultValue,
  currentFont,
  onFontChange,
}: ThemeFontSelectProps) {
  const [value, setValue] = useState(
    fontOptions[currentFont ?? ""] || defaultValue
  );

  useEffect(() => {
    setValue(fontOptions[currentFont ?? ""] || defaultValue);
  }, [currentFont, defaultValue, fontOptions]);

  const fontNames = useMemo(
    () => ["System", ...Object.keys(fontOptions)],
    [fontOptions]
  );

  const onValueChange = (newValue: string) => {
    setValue(newValue);
    onFontChange(newValue);
  };

  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger className="h-10 w-full cursor-pointer">
        <SelectValue placeholder="Select theme font" />
      </SelectTrigger>
      <SelectContent>
        {fontNames.map((fontName) => (
          <SelectItem
            key={fontName}
            value={fontOptions[fontName] ?? defaultValue}
          >
            <span
              style={{
                fontFamily: fontOptions[fontName] ?? defaultValue,
              }}
            >
              {fontName}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
